type Iteratee<T> = ((value: T) => any) | keyof T | undefined;

/**
 * Omit specified keys from an object, supporting nested keys using dot notation.
 *
 * @param obj The source object to omit keys from.
 * @param keysToOmit A string or array of strings representing keys to omit.
 * @returns A new object with specified keys omitted.
 */
export const omit = (object: Record<string, any>, paths: string | string[]): Record<string, any> => {
    if (object == null) {
        return {};
    }

    const castPath = (value: string | string[], object: any): string[] => {
        if (Array.isArray(value)) {
            return value;
        }
        if (typeof value === 'string' && object && value in object) {
            return [value];
        }
        return !value ? [''] : value.split('.').map(part => part.trim());
    };

    const getAllKeysIn = (object: any): string[] => {
        return Object.keys(object).concat(Object.getOwnPropertySymbols(object).map(symbol => symbol.toString()));
    };

    const baseClone = (value: any, flags: number): any => {
        if (value && typeof value === 'object') {
            const cloned = Array.isArray(value) ? [] : {};
            for (const key of Object.keys(value)) {
                cloned[key] = baseClone(value[key], flags);
            }
            return cloned;
        }
        return value;
    };

    const baseUnset = (object: any, path: string[]): boolean => {
        if (!object || typeof object !== 'object') return false;
        let current = object;
        for (let i = 0; i < path.length - 1; i++) {
            current = current[path[i]];
            if (!current) return false;
        }
        delete current[path[path.length - 1]];
        return true;
    };

    const isDeep = Array.isArray(paths) && paths.some(path => castPath(path, object).length > 1);
    const keysToOmit = Array.isArray(paths) ? paths : [paths];

    const castedPaths = keysToOmit.map(path => castPath(path, object));

    let result = {} as any;
    getAllKeysIn(object).forEach(key => {
        result[key] = object[key];
    });

    if (isDeep) {
        result = baseClone(result, 1 | 2 | 4);
    }

    castedPaths.forEach(path => {
        baseUnset(result, path);
    });

    return result;
};

/**
 * Perform a deep clone of a value, handling arrays, objects, and special objects like Date, Map, and Set.
 *
 * @param value The value to clone.
 * @returns A deep-cloned copy of the input value.
 */
export const cloneDeep = (value: unknown): unknown => {
    if (value == null || !isObject(value)) {
        return value;
    }

    // Handle special cases
    if (Array.isArray(value)) {
        return value.map(cloneDeep);
    }

    if (value instanceof Date) {
        return new Date(value.getTime());
    }

    if (value instanceof Set) {
        return new Set([...value].map(cloneDeep));
    }

    if (value instanceof Map) {
        return new Map([...value].map(([key, val]) => [cloneDeep(key), cloneDeep(val)]));
    }

    if (typeof value === 'object' && 'clone' in value && typeof (value as any).clone === 'function') {
        return (value as any).clone();
    }

    const result = Object.create(Object.getPrototypeOf(value));

    for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
            result[key] = cloneDeep((value as Record<string, unknown>)[key]);
        }
    }

    return result;
};

/**
 * Perform a simple deep clone using JSON serialization. Note: Does not handle special objects like Date, Map, or Set.
 *
 * @param oldObj The object to clone.
 * @returns A deep-cloned copy of the input object.
 */
export const simpleCloneDeep = oldObj => JSON.parse(JSON.stringify(oldObj));

/**
 * Get the last element of an array or string.
 *
 * @param input The array or string to retrieve the last element from.
 * @returns The last element, or undefined if input is invalid.
 */
export const last = input => {
    if (Array.isArray(input) || typeof input === 'string') {
        return input[input.length - 1];
    }
    return undefined;
};

/**
 * Check if a value is an object and not null or an array.
 *
 * @param value The value to check.
 * @returns `true` if the value is a plain object, otherwise `false`.
 */
export const isObject = (value: unknown): value is object => {
    return value !== null && typeof value === 'object';
};

/**
 * Check if a value is a plain object (created using `{}` or `Object.create(null)`).
 *
 * @param value The value to check.
 * @returns `true` if the value is a plain object, otherwise `false`.
 */
export const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === null || prototype === Object.prototype;
};

/**
 * A no-operation function that does nothing.
 */
export const noop = () => {
    // Do Nothing
};

/**
 * Pick specified keys from an object.
 *
 * @param obj The source object to pick keys from.
 * @param keys The keys to pick.
 * @returns A new object containing only the picked keys.
 */
export const pick = <T extends object, K extends keyof T>(obj: T, keys: any[]): Pick<T, K> => {
    const result = {} as Pick<T, K>;
    for (const key of keys) {
        if (key in obj) {
            result[key] = obj[key];
        }
    }
    return result;
};

/**
 * Convert a string to camelCase.
 *
 * @param str The input string.
 * @returns The camelCased string.
 */
export const camelCase = (str: string): string => {
    return str
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Separate camelCase words
        .replace(/[^a-zA-Z0-9]+/g, ' ') // Replace non-alphanumeric characters with space
        .trim() // Trim leading/trailing spaces
        .toLowerCase() // Convert to lowercase
        .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) => (index === 0 ? match.toLowerCase() : match.toUpperCase())) // Capitalize words after the first
        .replace(/\s+/g, ''); // Remove all spaces
};

/**
 * Perform a deep comparison between two values to determine if they are equivalent.
 *
 * @param value The first value.
 * @param other The second value.
 * @returns `true` if the values are deeply equal, otherwise `false`.
 */
export const isEqual = (value: any, other: any): boolean => {
    if (value === other) return true;
    if (typeof value !== typeof other) return false;
    if (value && other && typeof value === 'object') {
        if (Array.isArray(value) !== Array.isArray(other)) return false;
        const valueKeys = Object.keys(value);
        const otherKeys = Object.keys(other);
        if (valueKeys.length !== otherKeys.length) return false;
        for (const key of valueKeys) {
            if (!isEqual(value[key], other[key])) return false;
        }
        return true;
    }
    return false;
};

/**
 * Split an array into chunks of a specified size.
 *
 * @param array The array to chunk.
 * @param size The size of each chunk.
 * @returns An array of chunks.
 */
export const chunk = <T>(array: T[], size: number): T[][] => {
    if (size <= 0) throw new Error('Size must be greater than 0');

    const result: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
    }
    return result;
};

/**
 * Set a value at a specified path in an object, creating intermediate objects or arrays as needed.
 *
 * @param obj The target object.
 * @param path The path to set, as a string or array of keys.
 * @param value The value to set.
 * @returns The updated object.
 */
export const set = (obj: Record<string, any>, path: string | string[], value: any): Record<string, any> => {
    if (!obj || typeof obj !== 'object') {
        throw new Error('Target must be an object');
    }

    const keys = Array.isArray(path) ? path : path.match(/[^.[\]]+/g);
    if (!keys) {
        throw new Error('Invalid path');
    }

    let current = obj;

    for (let i = 0; i < keys.length; i++) {
        const key = isNaN(Number(keys[i])) ? keys[i] : Number(keys[i]);

        if (i === keys.length - 1) {
            current[key] = value;
        } else {
            if (current[key] === undefined || typeof current[key] !== 'object') {
                current[key] = isNaN(Number(keys[i + 1])) ? {} : [];
            }
            current = current[key];
        }
    }

    return obj;
};

/**
 * Check if a value is empty. Handles strings, arrays, objects, Maps, and Sets.
 *
 * @param value The value to check.
 * @returns `true` if the value is empty, otherwise `false`.
 */
export const isEmpty = (value: any): boolean => {
    if (value == null) return true;

    if (typeof value === 'string' || Array.isArray(value)) {
        return value.length === 0;
    }

    if (typeof value === 'object') {
        if (value instanceof Map || value instanceof Set) {
            return value.size === 0;
        }
        return Object.keys(value).length === 0;
    }

    return false;
};

/**
 * Get all values from an object.
 *
 * @param obj The input object.
 * @returns An array of the object's values.
 */
export const values = (obj: Record<string, any>): any[] => {
    if (obj == null || typeof obj !== 'object') {
        throw new Error('Input must be an object');
    }

    return Object.keys(obj).map(key => obj[key]);
};

/**
 * Flatten a two-dimensional array into a one-dimensional array.
 *
 * @param array The 2D array to flatten.
 * @returns A flattened array.
 */
export const flatten = <T>(array: T[][]): T[] => {
    if (!Array.isArray(array)) {
        throw new Error('Input must be an array');
    }

    return array.reduce((acc: T[], item: T | T[]) => acc.concat(item), []);
};

/**
 * Get unique elements from an array.
 *
 * @param array The array to process.
 * @returns A new array with only unique elements.
 */
export const uniq = <T>(array: T[]): T[] => {
    if (!Array.isArray(array)) {
        throw new Error('Input must be an array');
    }

    return Array.from(new Set(array));
};

/**
 * Capitalize the first letter of a string and convert the rest to lowercase.
 *
 * @param str The input string.
 * @returns The capitalized string.
 */
export const capitalize = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Generate a unique ID with an optional prefix.
 *
 * @param prefix The optional prefix for the ID.
 * @returns A unique ID string.
 */
export const uniqueId = (function () {
    let num = 0;
    return function (prefix) {
        prefix = String(prefix) || '';
        num += 1;
        return prefix + num;
    };
})();

/**
 * Retrieve the value at a specified path in an object.
 *
 * @param obj The source object.
 * @param path The path to the value, as a string or array of keys.
 * @param defaultValue The default value if the path does not exist.
 * @returns The value at the specified path or the default value.
 */
export const get = (obj: Record<string, any>, path: string | string[], defaultValue?: any): any => {
    if (!obj || typeof obj !== 'object') {
        return defaultValue;
    }

    const pathArray = Array.isArray(path) ? path : path.replace(/\[(\d+)]/g, '.$1').split('.');

    let result = obj;
    for (const key of pathArray) {
        if (result && key in result) {
            result = result[key];
        } else {
            return defaultValue;
        }
    }

    return result;
};

/**
 * Compute the difference between two arrays using a custom comparator.
 *
 * @param array The source array.
 * @param values The values to exclude.
 * @param comparator The comparator function.
 * @returns A new array containing elements not present in `values`.
 */
export const differenceWith = <T>(array: T[], values: T[], comparator: (a: T, b: T) => boolean): T[] => {
    if (!Array.isArray(array) || !Array.isArray(values)) {
        throw new TypeError('Both inputs must be arrays');
    }

    return array.filter(item => !values.some(value => comparator(item, value)));
};

/**
 * Recursively flatten a deeply nested array.
 *
 * @param array The nested array to flatten.
 * @returns A fully flattened array.
 */
export const flattenDeep = <T>(array: any[]): T[] => {
    if (!Array.isArray(array)) {
        throw new TypeError('Input must be an array');
    }

    return array.reduce((acc: T[], val) => {
        return Array.isArray(val) ? acc.concat(flattenDeep<T>(val)) : acc.concat(val);
    }, []);
};

/**
 * Update a value at a specified path in an object using an updater function.
 *
 * @param obj The target object.
 * @param path The path to the value, as a string or array of keys.
 * @param updater The updater function to apply to the value.
 * @returns The updated object.
 */
export const update = <T extends object>(obj: T, path: string | string[], updater: (value: any) => any): T => {
    if (!obj || typeof obj !== 'object') {
        throw new TypeError('The first argument must be an object');
    }

    const pathArray = Array.isArray(path) ? path : path.split('.');

    let current: any = obj;
    for (let i = 0; i < pathArray.length; i++) {
        const key = pathArray[i];

        if (i === pathArray.length - 1) {
            current[key] = updater(current[key]);
        } else {
            if (!current[key] || typeof current[key] !== 'object') {
                current[key] = {};
            }
            current = current[key];
        }
    }

    return obj;
};

/**
 * Compute the difference between two arrays using a transformation function.
 *
 * @param array The source array.
 * @param values The values to exclude.
 * @param iteratee The transformation function or property name.
 * @returns A new array containing elements not transformed to match `values`.
 */
export const differenceBy = <T>(array: T[], values: T[], iteratee?: Iteratee<T>): T[] => {
    if (!Array.isArray(array) || !Array.isArray(values)) {
        throw new TypeError('Both inputs must be arrays');
    }

    const getIterateeFunction = (iteratee: Iteratee<T>) => {
        if (typeof iteratee === 'function') {
            return iteratee;
        }
        if (typeof iteratee === 'string') {
            return (item: T) => (item as any)[iteratee];
        }
        return (item: T) => item;
    };

    const transform = getIterateeFunction(iteratee);
    const transformedValues = new Set(values.map(transform));

    return array.filter(item => !transformedValues.has(transform(item)));
};

type Callback<T, U> = (item: T, index: number, array: T[]) => U;
type Reducer<T, U> = (accumulator: U, currentValue: T, index: number, array: T[]) => U;
type CompareFunction<T> = (a: T, b: T) => number;

class Wrapper<T> {
    private value: T[];

    constructor(value: T[]) {
        this.value = value;
    }

    map<U>(callback: (item: T, index: number, array: T[]) => U): Wrapper<U> {
        this.value = this.value.map(callback as unknown as (item: T) => T);
        return this as unknown as Wrapper<U>;
    }

    filter(callback: (item: T, index: number, array: T[]) => boolean): Wrapper<T> {
        this.value = this.value.filter(callback);
        return this;
    }

    reduce<U>(callback: (accumulator: U, currentValue: T, index: number, array: T[]) => U, initialValue: U): U {
        return this.value.reduce(callback, initialValue);
    }

    sort(compareFunction?: (a: T, b: T) => number): Wrapper<T> {
        this.value = this.value.sort(compareFunction);
        return this;
    }

    valueOf(): T[] {
        return this.value;
    }
}

// Factory function to create a wrapper
export const createObjectWrapper = <T>(value: T[]): Wrapper<T> => {
    return new Wrapper(value);
};
